import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  useNodesState,
  useEdgesState,
  useReactFlow,
  Node,
  Edge,
  Connection,
  NodeChange,
  EdgeChange,
  MarkerType,
} from "reactflow";
import {
  Workflow,
  WorkflowStep,
  WorkflowTrigger,
  CreateUpdateWorkflow,
  CreateUpdateWorkflowStep,
  EditorState,
} from "@/types/breeze/Workflow";
import { workflowService } from "@/services/breeze/WorkflowService";
import { toast } from "react-hot-toast";
import { messageFromError } from "@/utils/text";

function extractServerMessage(error: unknown): string | null {
  const anyErr = error as { response?: { data?: unknown }; message?: string } | null;
  const data = anyErr?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    const m = (data as { error?: string; message?: string }).error
      ?? (data as { error?: string; message?: string }).message;
    if (typeof m === "string") return m;
  }
  return anyErr?.message ? messageFromError(error, "") || null : null;
}

// #region Helper Functions

/**
 * Creates a React Flow node for a given workflow step.
 * @param step The workflow step.
 * @param position The position of the node on the canvas.
 * @param handlers Handlers for edit and delete actions.
 * @returns A React Flow Node object.
 */
const createFlowNode = (
  step: WorkflowStep,
  position: { x: number; y: number },
  handlers: {
    onStepEdit: (step: WorkflowStep) => void;
    onStepDelete: (step: WorkflowStep) => void;
  },
): Node => ({
  id: step.key,
  type: "stepNode",
  position,
  data: {
    step,
    onEditClicked: () => handlers.onStepEdit(step),
    onDeleteClicked: () => handlers.onStepDelete(step),
  },
});

/**
 * Creates a React Flow edge for a step relationship (success or failure).
 * @param sourceStep The source workflow step.
 * @param targetStepKey The key of the target workflow step.
 * @param type The type of the connection ('success' or 'failure').
 * @returns A React Flow Edge object.
 */
const createFlowEdge = (
  sourceStep: WorkflowStep,
  targetStepKey: string,
  type: "success" | "failure",
): Edge => {
  const colors = {
    success: "var(--green-9)",
    failure: "var(--red-9)",
  };
  const color = colors[type];

  return {
    id: `${sourceStep.key}-${type}-${targetStepKey}`,
    type: "smoothstep",
    source: sourceStep.key,
    target: targetStepKey,
    sourceHandle: type,
    animated: true,
    style: { stroke: color, strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color },
  };
};

/**
 * Creates the dedicated "Start" node for the workflow.
 * @param position The position of the node on the canvas.
 * @returns A React Flow Node object.
 */
const createStartNode = (
  position: { x: number; y: number },
  triggers: WorkflowTrigger[],
  onTest?: () => void,
): Node => ({
  id: "start-node",
  type: "startNode",
  position,
  data: { onTest, triggers },
  draggable: true,
  selectable: false,
  deletable: false,
});

/**
 * Creates an edge from the "Start" node to the designated start step.
 * @param targetStepKey The key of the start step.
 * @returns A React Flow Edge object.
 */
const createStartEdge = (targetStepKey: string): Edge => ({
  id: `start-edge-${targetStepKey}`,
  type: "smoothstep",
  source: "start-node",
  target: targetStepKey,
  sourceHandle: "start",
  animated: true,
  style: { stroke: "var(--accent-9)", strokeWidth: 3 },
  markerEnd: { type: MarkerType.ArrowClosed, color: "var(--accent-9)" },
});

/**
 * Calculates a reasonable default position for a new node.
 * Tries to place it in the center of the current viewport.
 * @param getViewport A function to get the current viewport from React Flow.
 * @returns A position object { x, y }.
 */
const calculateViewportCenter = (
  getViewport: () => { x: number; y: number; zoom: number },
) => {
  const viewport = getViewport();
  const viewportWidth = window.innerWidth || 800;
  const viewportHeight = window.innerHeight || 600;

  return {
    x: (-viewport.x + viewportWidth / 2) / viewport.zoom,
    y: (-viewport.y + viewportHeight / 2) / viewport.zoom,
  };
};

/**
 * Finds a non-overlapping position for a new node.
 */
const findNonOverlappingPosition = (
  basePosition: { x: number; y: number },
  existingPositions: Record<string, { x: number; y: number }>,
): { x: number; y: number } => {
  const NODE_WIDTH = 280;
  const NODE_HEIGHT = 120;
  const GAP = 40;
  let pos = { ...basePosition };
  let attempts = 0;

  while (attempts < 50) {
    const overlaps = Object.values(existingPositions).some(
      (existing) =>
        Math.abs(existing.x - pos.x) < NODE_WIDTH + GAP &&
        Math.abs(existing.y - pos.y) < NODE_HEIGHT + GAP
    );
    if (!overlaps) return pos;
    pos = {
      x: basePosition.x + ((attempts % 4) + 1) * (NODE_WIDTH + GAP),
      y: basePosition.y + Math.floor(attempts / 4) * (NODE_HEIGHT + GAP),
    };
    attempts++;
  }
  return pos;
};

/**
 * Safely parses the persisted editor state.
 */
const parseEditorState = (rawState?: string | null): EditorState | null => {
  if (!rawState) return null;
  try {
    return JSON.parse(rawState);
  } catch (error) {
    console.warn("Failed to parse editor state:", error);
    return null;
  }
};

/**
 * Builds flow nodes and edges from workflow steps plus persisted editor state.
 * Keeps layout concerns in one place so the hook can stay lean.
 */
const buildGraphFromSteps = (
  steps: WorkflowStep[],
  triggers: WorkflowTrigger[],
  editorState: EditorState | null,
  getViewport: () => { x: number; y: number; zoom: number },
  handlers: {
    onStepEdit: (step: WorkflowStep) => void;
    onStepDelete: (step: WorkflowStep) => void;
    onTest?: () => void;
  },
  currentNodePositions: Record<string, { x: number; y: number }>,
): { nodes: Node[]; edges: Edge[]; viewport?: EditorState["viewport"] } => {
  const nodePositions = editorState?.nodePositions || {};
  const startNodePosition = currentNodePositions["start-node"] ||
    nodePositions["start-node"] || { x: 50, y: 200 };
  const nodes: Node[] = [
    createStartNode(startNodePosition, triggers, handlers.onTest),
  ];
  const edges: Edge[] = [];

  const placedPositions: Record<string, { x: number; y: number }> = {
    "start-node": startNodePosition,
  };

  steps.forEach((step) => {
    let position = currentNodePositions[step.key] || nodePositions[step.key];
    if (!position) {
      const center = calculateViewportCenter(getViewport);
      position = findNonOverlappingPosition(center, placedPositions);
    }
    placedPositions[step.key] = position;
    nodes.push(createFlowNode(step, position, handlers));
  });

  const startStep = steps.find((step) => step.is_start_step);
  if (startStep) {
    edges.push(createStartEdge(startStep.key));
  }

  const attachEdge = (
    sourceStep: WorkflowStep,
    targetId: number | null | undefined,
    type: "success" | "failure",
  ) => {
    if (!targetId) return;
    const target = steps.find((s) => s.id === targetId);
    if (target) edges.push(createFlowEdge(sourceStep, target.key, type));
  };

  steps.forEach((step) => {
    attachEdge(step, step.on_success_step_id, "success");
    attachEdge(step, step.on_failure_step_id, "failure");
  });

  return { nodes, edges, viewport: editorState?.viewport };
};

// #endregion

/**
 * `useWorkflowEditor` is a complex hook that manages the entire state and logic for the workflow editor.
 * It handles React Flow state, workflow data, API interactions, and undo/redo functionality.
 *
 * @param workflow The initial workflow data object.
 * @param onWorkflowUpdate A callback function that is triggered when the workflow data is updated,
 * allowing the parent component to refetch data.
 */
export function useWorkflowEditor(
  workflow: Workflow,
  onWorkflowUpdate?: () => void,
  onTest?: () => void,
) {
  // #region State Management

  const { getViewport, setViewport } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // A local copy of the workflow steps to allow for optimistic updates in the UI.
  const [localSteps, setLocalSteps] = useState<WorkflowStep[]>(workflow.steps);

  // UI-related state, such as loading indicators, drawer visibility, and unsaved changes flags.
  const [uiState, setUiState] = useState({
    isLoading: false,
    hasUnsavedChanges: false,
    selectedEdge: null as Edge | null,
    selectedStep: null as WorkflowStep | null,
    drawers: { addStep: false, editStep: false },
    showJobHistory: false,
    showSettings: false,
  });

  // State for workflow metadata like name and description.
  const [metadata, setMetadata] = useState({
    name: workflow.name,
    description: workflow.description || "",
  });

  // State for the undo/redo history.
  const [history, setHistory] = useState<{
    past: Array<{ nodes: Node[]; edges: Edge[]; steps: WorkflowStep[] }>;
    future: Array<{ nodes: Node[]; edges: Edge[]; steps: WorkflowStep[] }>;
  }>({ past: [], future: [] });
  const hasAppliedInitialViewport = useRef(false);
  const latestGraphRef = useRef<{
    nodes: Node[];
    edges: Edge[];
    steps: WorkflowStep[];
  }>({
    nodes,
    edges,
    steps: localSteps,
  });

  useEffect(() => {
    latestGraphRef.current = { nodes, edges, steps: localSteps };
  }, [nodes, edges, localSteps]);

  const markDirty = useCallback(() => {
    setUiState((s) => ({ ...s, hasUnsavedChanges: true }));
  }, []);

  const pushHistory = useCallback(() => {
    setHistory((curr) => ({
      past: [...curr.past, latestGraphRef.current],
      future: [],
    }));
  }, []);

  const runWithLoading = useCallback(async (operation: () => Promise<void>) => {
    setUiState((s) => ({ ...s, isLoading: true }));
    try {
      await operation();
    } finally {
      setUiState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  // #endregion

  // #region Workflow Mutations

  const updateStep = useCallback(
    async (stepId: number, data: CreateUpdateWorkflowStep) => {
      try {
        await runWithLoading(async () => {
          await workflowService.updateWorkflowStep(workflow.id, stepId, data);
          if (onWorkflowUpdate) onWorkflowUpdate();
          toast.success("Step updated");
        });
      } catch (error) {
        console.error("Failed to update step:", error);
        const detail = extractServerMessage(error);
        toast.error(detail ? `Failed to update step: ${detail}` : "Failed to update step.");
      }
    },
    [workflow.id, onWorkflowUpdate, runWithLoading],
  );

  const setAsStartStep = useCallback(
    async (newStartStep: WorkflowStep) => {
      try {
        await runWithLoading(async () => {
          const currentStartStep = localSteps.find((s) => s.is_start_step);

          const updates: Promise<any>[] = [];
          if (currentStartStep && currentStartStep.id !== newStartStep.id) {
            updates.push(
              workflowService.updateWorkflowStep(
                workflow.id,
                currentStartStep.id,
                {
                  ...currentStartStep,
                  is_start_step: false,
                },
              ),
            );
          }

          updates.push(
            workflowService.updateWorkflowStep(workflow.id, newStartStep.id, {
              ...newStartStep,
              is_start_step: true,
            }),
          );

          await Promise.all(updates);

          setEdges((eds) => [
            ...eds.filter((e) => !e.id.startsWith("start-edge-")),
            createStartEdge(newStartStep.key),
          ]);

          if (onWorkflowUpdate) onWorkflowUpdate();
          toast.success(`'${newStartStep.name}' is now the start step.`);
        });
      } catch (error) {
        console.error("Failed to set start step:", error);
        toast.error("Failed to set start step.");
      }
    },
    [localSteps, workflow.id, onWorkflowUpdate, runWithLoading],
  );

  const handleStepDelete = useCallback(
    async (stepToDelete: WorkflowStep) => {
      pushHistory();
      try {
        await runWithLoading(async () => {
          await workflowService.deleteWorkflowStep(
            workflow.id,
            stepToDelete.id,
          );
          if (onWorkflowUpdate) onWorkflowUpdate();
          toast.success("Step deleted.");
        });
      } catch (error) {
        console.error("Failed to delete step:", error);
        toast.error("Failed to delete step.");
      }
    },
    [workflow.id, onWorkflowUpdate, pushHistory, runWithLoading],
  );

  const handleStepCreated = useCallback(
    async (stepFormData: WorkflowStep) => {
      try {
        await runWithLoading(async () => {
          const isFirstStep = localSteps.length === 0;
          const hasStartStep = localSteps.some((s) => s.is_start_step);

          const stepData: CreateUpdateWorkflowStep = {
            ...stepFormData,
            is_start_step: isFirstStep && !hasStartStep,
          };

          await workflowService.createWorkflowStep(workflow.id, stepData);
          if (onWorkflowUpdate) onWorkflowUpdate();
          toast.success("Step created successfully.");
          setUiState((s) => ({
            ...s,
            drawers: { ...s.drawers, addStep: false },
          }));
        });
      } catch (error) {
        console.error("Failed to create step:", error);
        const detail = extractServerMessage(error);
        toast.error(detail ? `Failed to create step: ${detail}` : "Failed to create step.");
      }
    },
    [localSteps, workflow.id, onWorkflowUpdate, runWithLoading],
  );

  const handleStepUpdated = useCallback(
    async (stepId: number, stepFormData: WorkflowStep) => {
      await updateStep(stepId, stepFormData);
      setUiState((s) => ({
        ...s,
        selectedStep: null,
        drawers: { ...s.drawers, editStep: false },
      }));
    },
    [updateStep],
  );

  const deleteSelectedConnection = useCallback(async () => {
    if (!uiState.selectedEdge) return;
    pushHistory();

    const { source, sourceHandle } = uiState.selectedEdge;
    const sourceStep = localSteps.find((s) => s.key === source);
    if (!sourceStep) return;

    const fieldToUpdate =
      sourceHandle === "success" ? "on_success_step_id" : "on_failure_step_id";

    await updateStep(sourceStep.id, { ...sourceStep, [fieldToUpdate]: null });
  }, [uiState.selectedEdge, localSteps, pushHistory, updateStep]);

  // #endregion

  // #region React Flow Handlers

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      const hasPositionChange = changes.some(
        (c) => c.type === "position" && c.dragging === false,
      );
      if (hasPositionChange) {
        markDirty();
      }
    },
    [onNodesChange, markDirty],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);
      const selectionChange = changes.find((c) => c.type === "select");
      if (selectionChange) {
        setUiState((s) => ({
          ...s,
          selectedEdge: selectionChange.selected
            ? edges.find((e) => e.id === selectionChange.id) || null
            : null,
        }));
      }
    },
    [edges, onEdgesChange],
  );

  const handleConnect = useCallback(
    async (connection: Connection) => {
      const { source, sourceHandle, target } = connection;
      if (!source || !target || !sourceHandle) return;

      pushHistory();

      const targetStep = localSteps.find((s) => s.key === target);
      if (!targetStep) return toast.error("Target step not found");

      if (source === "start-node") {
        await setAsStartStep(targetStep);
        return;
      }

      const sourceStep = localSteps.find((s) => s.key === source);
      if (!sourceStep) return toast.error("Source step not found");

      const connectionType = sourceHandle as "success" | "failure";
      const fieldToUpdate =
        connectionType === "success"
          ? "on_success_step_id"
          : "on_failure_step_id";

      await updateStep(sourceStep.id, {
        ...sourceStep,
        [fieldToUpdate]: targetStep.id,
      });
    },
    [localSteps, pushHistory, updateStep, setAsStartStep],
  );

  // #endregion

  // #region Effects

  useEffect(() => {
    setLocalSteps(workflow.steps);
  }, [workflow.steps]);

  const handleStepEdit = useCallback(
    (step: WorkflowStep) =>
      setUiState((s) => ({
        ...s,
        selectedStep: step,
        drawers: { ...s.drawers, editStep: true },
      })),
    [],
  );

  const nodeHandlers = useMemo(
    () => ({
      onStepEdit: handleStepEdit,
      onStepDelete: handleStepDelete,
      onTest,
    }),
    [handleStepEdit, handleStepDelete, onTest],
  );

  useEffect(() => {
    const parsedEditorState = parseEditorState(workflow.editor_state);
    const currentNodePositions = latestGraphRef.current.nodes.reduce(
      (acc, node) => {
        acc[node.id] = { x: node.position.x, y: node.position.y };
        return acc;
      },
      {} as Record<string, { x: number; y: number }>,
    );

    const graph = buildGraphFromSteps(
      localSteps,
      workflow.triggers ?? [],
      parsedEditorState,
      getViewport,
      nodeHandlers,
      currentNodePositions,
    );

    setNodes(graph.nodes);
    setEdges(graph.edges);

    if (graph.viewport && !hasAppliedInitialViewport.current) {
      setViewport(graph.viewport);
      hasAppliedInitialViewport.current = true;
    }
  }, [
    localSteps,
    workflow.editor_state,
    workflow.triggers,
    getViewport,
    nodeHandlers,
    setViewport,
  ]);

  useEffect(() => {
    hasAppliedInitialViewport.current = false;
  }, [workflow.id]);

  // #endregion

  // #region Workflow-level Actions

  const saveWorkflow = useCallback(async () => {
    try {
      await runWithLoading(async () => {
        const nodePositions = nodes.reduce(
          (acc, node) => {
            acc[node.id] = { x: node.position.x, y: node.position.y };
            return acc;
          },
          {} as Record<string, { x: number; y: number }>,
        );

        const editorStateData: EditorState = {
          nodePositions,
          viewport: getViewport(),
          version: 1,
          lastSaved: new Date().toISOString(),
        };

        const workflowUpdatePayload: CreateUpdateWorkflow = {
          name: metadata.name,
          description: metadata.description,
          group: workflow.group,
          enabled: workflow.enabled,
          editor_state: JSON.stringify(editorStateData),
          triggers: workflow.triggers.map((t) => ({
            type: t.type,
            enabled: t.enabled,
            config: t.config,
          })),
        };

        const editorStatePromise = workflowService.updateWorkflow(
          workflow.id,
          workflowUpdatePayload,
        );

        const stepUpdatePromises: Promise<any>[] = [];
        if (uiState.hasUnsavedChanges) {
          localSteps.forEach((currentStep) => {
            const originalStep = workflow.steps.find(
              (s) => s.id === currentStep.id,
            );
            if (
              originalStep &&
              JSON.stringify(currentStep) !== JSON.stringify(originalStep)
            ) {
              stepUpdatePromises.push(
                workflowService.updateWorkflowStep(
                  workflow.id,
                  currentStep.id,
                  currentStep,
                ),
              );
            }
          });
        }

        await Promise.all([editorStatePromise, ...stepUpdatePromises]);

        toast.success("Workflow saved.");
        setUiState((s) => ({ ...s, hasUnsavedChanges: false }));
        if (onWorkflowUpdate) onWorkflowUpdate();
      });
    } catch (error) {
      console.error("Failed to save workflow:", error);
      toast.error("Failed to save workflow.");
    }
  }, [
    nodes,
    getViewport,
    workflow,
    metadata,
    uiState.hasUnsavedChanges,
    localSteps,
    onWorkflowUpdate,
    runWithLoading,
  ]);

  // #endregion

  // #region Undo/Redo

  const undo = useCallback(() => {
    if (history.past.length === 0) return;
    const lastState = history.past[history.past.length - 1];
    setHistory({
      past: history.past.slice(0, -1),
      future: [{ nodes, edges, steps: localSteps }, ...history.future],
    });
    setNodes(lastState.nodes);
    setEdges(lastState.edges);
    setLocalSteps(lastState.steps);
    setUiState((s) => ({ ...s, hasUnsavedChanges: true }));
  }, [history, nodes, edges, localSteps]);

  const redo = useCallback(() => {
    if (history.future.length === 0) return;
    const nextState = history.future[0];
    setHistory({
      past: [...history.past, { nodes, edges, steps: localSteps }],
      future: history.future.slice(1),
    });
    setNodes(nextState.nodes);
    setEdges(nextState.edges);
    setLocalSteps(nextState.steps);
    setUiState((s) => ({ ...s, hasUnsavedChanges: true }));
  }, [history, nodes, edges, localSteps]);

  // #endregion

  return {
    // State
    nodes,
    edges,
    workflowState: {
      ...workflow,
      ...metadata,
      ...uiState,
      steps: localSteps,
    },

    // Handlers
    handleNodesChange,
    handleEdgesChange,
    handleConnect,
    handleStepCreated,
    handleStepUpdated,
    deleteSelectedConnection,
    saveWorkflow,
    onNameChange: (name: string) => {
      setMetadata((m) => ({ ...m, name }));
      setUiState((s) => ({ ...s, hasUnsavedChanges: true }));
    },
    onDescriptionChange: (description: string) => {
      setMetadata((m) => ({ ...m, description }));
      setUiState((s) => ({ ...s, hasUnsavedChanges: true }));
    },
    setUiState,
    runWithLoading,

    // Undo/Redo
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    undo,
    redo,
  };
}
